import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, XCircle } from "lucide-react";

export default function PricingSuccess() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [info, setInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let mounted = true;
    const poll = async (n) => {
      if (!mounted) return;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setInfo(res.data);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
      } catch (e) {
        console.warn("poll error", e);
      }
      if (n >= 8) {
        setState("error");
        return;
      }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2200);
    };
    poll(0);
    return () => {
      mounted = false;
    };
  }, [sessionId]);

  return (
    <div className="max-w-[720px] mx-auto px-6 lg:px-10 pt-36 pb-16" data-testid="page-pricing-success">
      <div className="surface p-8 md:p-10 text-center">
        {state === "polling" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-gold-500/10 border border-gold-500/30 grid place-items-center">
              <Loader2 size={24} className="text-gold-400 animate-spin" />
            </div>
            <h1 className="mt-6 font-display text-3xl tracking-tight">
              {lang === "pt" ? "Confirmando o pagamento…" : "Confirming your payment…"}
            </h1>
            <p className="mt-3 text-sm text-neutral-400">
              {lang === "pt"
                ? `Isso leva poucos segundos. (tentativa ${attempts + 1}/8)`
                : `This takes a few seconds. (attempt ${attempts + 1}/8)`}
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-gold-500/15 border border-gold-500/40 grid place-items-center shadow-[0_0_30px_rgba(233,185,52,0.22)]">
              <CheckCircle2 size={28} className="text-gold-400" />
            </div>
            <h1 className="mt-6 font-display text-4xl tracking-tight brand-metal">
              {lang === "pt" ? "Bem-vindo ao GOLDORA!" : "Welcome to GOLDORA!"}
            </h1>
            <p className="mt-3 text-neutral-300">
              {lang === "pt"
                ? `Seu plano ${info?.plan ? info.plan.toUpperCase() : ""} está ativo.`
                : `Your ${info?.plan ? info.plan.toUpperCase() : ""} plan is active.`}
            </p>
            {info?.expires_at && (
              <p className="mt-2 text-xs text-neutral-500 font-mono uppercase tracking-widest">
                {lang === "pt" ? "Válido até" : "Valid until"}{" "}
                {new Date(info.expires_at).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US")}
              </p>
            )}
            <Link
              to="/"
              data-testid="success-home"
              className="mt-8 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-6 py-3 rounded-md transition-colors"
            >
              {lang === "pt" ? "Começar a decidir" : "Start deciding"}
              <ArrowRight size={16} />
            </Link>
          </>
        )}

        {(state === "expired" || state === "error") && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-red-500/10 border border-red-500/30 grid place-items-center">
              <XCircle size={24} className="text-red-400" />
            </div>
            <h1 className="mt-6 font-display text-3xl tracking-tight">
              {lang === "pt" ? "Não confirmamos o pagamento" : "Payment not confirmed"}
            </h1>
            <p className="mt-3 text-sm text-neutral-400">
              {lang === "pt"
                ? "A sessão expirou ou o pagamento não foi concluído. Você pode tentar novamente."
                : "The session expired or the payment wasn't completed. You can try again."}
            </p>
            <Link
              to="/planos"
              data-testid="success-retry"
              className="mt-8 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-6 py-3 rounded-md transition-colors"
            >
              {lang === "pt" ? "Voltar aos planos" : "Back to plans"}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
